"use client";

import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

import type { Region } from "@/lib/region";
import { useRegionSwitchHrefs } from "@/lib/region-paths-context";
import { settleRegionChoice } from "@/lib/region-switch";
import { cn } from "@/lib/utils";

const REGIONS: { value: Region; label: string }[] = [
  { value: "ie", label: "Ireland" },
  { value: "uk", label: "United Kingdom" },
];

export function RegionSwitcher({
  region,
  variant = "overlay",
}: {
  region: Region;
  variant?: "overlay" | "solid";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hrefs = useRegionSwitchHrefs();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Region: ${region === "uk" ? "United Kingdom" : "Ireland"}`}
        className="inline-flex cursor-pointer items-center justify-center rounded-full p-1 transition-opacity hover:opacity-80"
      >
        <span
          aria-hidden
          className={cn(
            "inline-block size-5 shrink-0 overflow-hidden rounded-full bg-cover bg-center",
            variant === "overlay"
              ? "shadow-[0_0_0_1px_rgba(255,255,255,0.35)]"
              : "shadow-[0_0_0_1px_rgba(255,255,255,0.25)]",
          )}
          style={{ backgroundImage: `url(/assets/region/${region}.svg)` }}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="border-navy-03/6 text-navy-03 absolute top-full right-0 z-50 mt-3 w-60 rounded-[20px] border bg-white p-3 shadow-[0_30px_60px_-20px_rgba(5,29,45,0.18),0_8px_16px_-4px_rgba(5,29,45,0.06)]"
        >
          <div className="text-navy-03/50 px-2 pt-1 pb-2 text-[11px] font-bold tracking-widest uppercase">
            Region
          </div>
          {REGIONS.map((r) => (
            <a
              key={r.value}
              href={hrefs[r.value]}
              role="menuitem"
              onClick={() => {
                settleRegionChoice();
                setOpen(false);
              }}
              className={cn(
                "hover:bg-teal-01 flex w-full cursor-pointer items-center gap-2.5 rounded-[10px] px-2 py-2 text-sm transition-colors",
                region === r.value ? "bg-teal-01 font-semibold" : "text-navy-03/75",
              )}
            >
              <span
                aria-hidden
                className="inline-block size-4 shrink-0 overflow-hidden rounded-full bg-cover bg-center shadow-[0_0_0_1px_rgba(5,29,45,0.08)]"
                style={{ backgroundImage: `url(/assets/region/${r.value}.svg)` }}
              />
              <span className="flex-1 truncate text-left">{r.label}</span>
              {region === r.value ? (
                <Check className="text-teal-07 size-4 shrink-0" strokeWidth={2.5} />
              ) : null}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
